import { Building2, Plus, Search, Users } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NovoProcessoModal } from "@/components/dashboard/NovoProcessoModal";

export type ModuleConfig = {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  bgLight: string;
  searchFields: { value: string; label: string }[];
  pipeline: { label: string; value: number; href: string }[];
  newProcessHref: string;
  activeCount?: number;
  clientCount?: number;
};

/** NIPSYS brand palette — navy (#1B3A5C) + orange (#E8721A) */
const NAVY = "#1B3A5C";
const ORANGE = "#E8721A";

/** Each module gets a unique icon/header tint, stats use navy */
const MODULE_COLORS: Record<string, { icon: string; iconBg: string; border: string }> = {
  marcas:        { icon: NAVY,      iconBg: "rgba(27,58,92,0.08)",  border: "rgba(27,58,92,0.12)" },
  patentes:      { icon: ORANGE,    iconBg: "rgba(232,114,26,0.08)", border: "rgba(232,114,26,0.12)" },
  di:            { icon: "#0F766E", iconBg: "rgba(15,118,110,0.08)", border: "rgba(15,118,110,0.12)" },
  internacional: { icon: "#6D28D9", iconBg: "rgba(109,40,217,0.08)", border: "rgba(109,40,217,0.12)" },
};
const FALLBACK_COLORS = MODULE_COLORS.marcas;

export function ModuleCard({ config }: { config: ModuleConfig }) {
  const [searchField, setSearchField] = useState(config.searchFields[0]?.value ?? "");
  const [searchQuery, setSearchQuery] = useState("");
  const [novoProcessoOpen, setNovoProcessoOpen] = useState(false);
  const navigate = useNavigate();
  const Icon = config.icon;
  const mc = MODULE_COLORS[config.id] || FALLBACK_COLORS;

  const moduleBasePaths: Record<string, string> = {
    marcas: "/marcas", patentes: "/patentes", di: "/di", internacional: "/internacional",
  };
  const basePath = moduleBasePaths[config.id] || `/${config.id}`;

  return (
    <div
      className="flex flex-col p-5 border transition-all duration-300 relative overflow-hidden group bg-white"
      style={{ borderColor: mc.border, borderRadius: 16, boxShadow: '0 1px 3px rgba(13,46,69,0.04), 0 4px 12px rgba(13,46,69,0.02)' }}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div
          className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate(basePath)}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: mc.iconBg }}>
            <Icon className="h-4 w-4" style={{ color: mc.icon }} />
          </div>
          <h3 className="text-[15px] font-bold text-slate-800">{config.label}</h3>
        </div>
        <button
          onClick={() => setNovoProcessoOpen(true)}
          className="flex h-7 items-center gap-1 rounded-lg px-2.5 text-[11px] font-semibold transition-all hover:opacity-80"
          style={{ background: "rgba(232,114,26,0.12)", color: ORANGE }}
        >
          <Plus className="h-3 w-3" />
          Novo
        </button>
      </div>

      {/* Meta badges */}
      <div className="flex items-center gap-2 mb-4 text-[10px] font-medium">
        <div className="flex items-center gap-1.5 rounded-md px-2 py-1" style={{ background: mc.iconBg, color: mc.icon }}>
          <Users className="h-3 w-3" />
          {config.activeCount ?? 0} ativos
        </div>
        <div className="flex items-center gap-1.5 rounded-md px-2 py-1" style={{ background: "rgba(232,114,26,0.08)", color: ORANGE }}>
          <Building2 className="h-3 w-3" />
          {config.clientCount ?? 0} clientes
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex gap-2">
        <select
          value={searchField}
          onChange={(event) => setSearchField(event.target.value)}
          className="min-w-[95px] h-8 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-400 focus:outline-none"
        >
          {(config.searchFields || []).map((field) => (
            <option key={field.value} value={field.value}>{field.label}</option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Buscar..."
            className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 text-[11px] text-slate-800 placeholder:text-slate-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Pipeline stats grid — alternates navy and orange */}
      <div className="grid grid-cols-3 gap-2 mt-2">
        {(config.pipeline || []).map((stage, i) => {
          const isNavy = i % 2 === 0;
          const numColor = isNavy ? NAVY : ORANGE;
          const bgColor = isNavy ? "rgba(27,58,92,0.05)" : "rgba(232,114,26,0.05)";
          const borderColor = isNavy ? "rgba(27,58,92,0.12)" : "rgba(232,114,26,0.12)";
          return (
            <Link
              key={stage.label}
              to={stage.href}
              className="rounded-xl px-2 py-3 text-center transition-all hover:shadow-sm hover:scale-[1.02] border"
              style={{ background: bgColor, borderColor }}
            >
              <p className="truncate text-[11px] text-slate-400 font-medium mb-1">{stage.label}</p>
              <p className="text-[16px] font-bold" style={{ color: numColor }}>{stage.value}</p>
            </Link>
          );
        })}
      </div>

      <NovoProcessoModal open={novoProcessoOpen} onClose={() => setNovoProcessoOpen(false)} moduleId={config.id} moduleLabel={config.label} />
    </div>
  );
}
